import React, { useState } from 'react'
import styled from 'styled-components'

interface Feature {
  icon: React.ReactNode
  name: string
}

export interface PricingCardProps {
  title: string
  titleIcon: React.ReactNode
  monthlyPrice: number
  yearlyPrice: number
  originalYearlyPrice?: number
  features: Feature[]
  advancedModels: Feature[]
  description: string
  onSubscribe: () => void
}

interface SubscribeButtonProps {
  yearly?: boolean
}

const Card = styled.div`
  padding: 24px;
  border-radius: 16px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-width: 400px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 32px;
`

const Title = styled.h2`
  color: #393d7b;
  font-size: 20px;
  margin: 0;
`

const PriceSection = styled.div`
  margin-bottom: 56px;
  text-align: left;
`

const Price = styled.div`
  font-size: 34px;
  font-weight: bold;
  color: #07091e;
  font-family: Rubik;
`

const Period = styled.span`
  color: #666;
  font-size: 16px;
`

const OriginalPrice = styled.span`
  text-decoration: line-through;
  color: #999;
  margin-left: 8px;
`

const MonthlyEquivalent = styled.div`
  color: #666;
  font-size: 14px;
  margin-top: 8px;
`

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  color: #ff5722;
`

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
`

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: #ff5722;
  }

  &:checked + span:before {
    transform: translateX(24px);
  }
`

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;

  &:before {
    position: absolute;
    content: '';
    height: 20px;
    width: 20px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }
`

const SubscribeButton = styled.button<SubscribeButtonProps>`
  width: 100%;
  padding: 16px;
  border-radius: 32px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: ${(props) => (props.yearly ? '#ff5722' : '#1a237e')};
  color: white;
  font-size: 16px;
  cursor: pointer;
  margin-bottom: 24px;
`

const SectionTitle = styled.h3`
  font-size: 18px;
  margin: 24px 0 16px;
  text-align: left;
`

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const PricingCard: React.FC<PricingCardProps> = ({
  title,
  titleIcon,
  monthlyPrice,
  yearlyPrice,
  originalYearlyPrice,
  features,
  advancedModels,
  description,
  onSubscribe,
}) => {
  const [isYearly, setIsYearly] = useState(false)

  return (
    <Card>
      <Header>
        {titleIcon}
        <Title>{title}</Title>
      </Header>

      <PriceSection>
        <Price>
          US${isYearly ? yearlyPrice : monthlyPrice}
          {isYearly && originalYearlyPrice && <OriginalPrice>US${originalYearlyPrice}</OriginalPrice>}
          <Period>/{isYearly ? 'year' : 'month'}</Period>
        </Price>
        {isYearly && <MonthlyEquivalent>US${(yearlyPrice / 12).toFixed(1)}/month</MonthlyEquivalent>}
      </PriceSection>

      <ToggleContainer>
        Save 50% with annual billing
        <Toggle>
          <ToggleInput type="checkbox" checked={isYearly} onChange={() => setIsYearly(!isYearly)} />
          <Slider />
        </Toggle>
      </ToggleContainer>

      <SubscribeButton yearly={isYearly} onClick={onSubscribe}>
        Subscribe {isYearly ? 'Now' : ''}
      </SubscribeButton>

      <SectionTitle>Unlock All Premium Features</SectionTitle>
      <FeatureList>
        {features.map((feature, index) => (
          <FeatureItem key={index}>
            {feature.icon}
            <span>{feature.name}</span>
          </FeatureItem>
        ))}
      </FeatureList>

      <SectionTitle>Unlimited access to advanced models</SectionTitle>
      <FeatureList>
        {advancedModels.map((model, index) => (
          <FeatureItem key={index}>
            {model.icon}
            <span>{model.name}</span>
          </FeatureItem>
        ))}
      </FeatureList>

      <SectionTitle>{description}</SectionTitle>
    </Card>
  )
}

export default PricingCard
